import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FiEye, FiGlobe, FiPlus, FiCheck } from 'react-icons/fi';
import { toast } from '../../Toast';
import { BackLink, HubHeader, hubPath, useHubSite } from '../shared';
import { NEWS_TYPES, NewsType } from './NewsMedia';
import PublishingCard, { PublishWhen } from './news/PublishingCard';
import PictureCard from './news/PictureCard';
import ArticleForm from './news/ArticleForm';
import { LinkFormProps } from './news/LinkCards';
import VideoForm from './news/VideoForm';
import PodcastForm from './news/PodcastForm';
import WebinarForm from './news/WebinarForm';
import CoverageForm from './news/CoverageForm';
import { getArticleSample, getLinkSample } from './news/newsSamples';

const TYPES = Object.keys(NEWS_TYPES) as NewsType[];

// per type: tile sub line, page title (add / edit) and subtitle
const PAGES: Record<NewsType, { tile: string; add: string; edit: string; sub: (host: string, short: string) => string }> = {
    article: { tile: 'Write it here in Investi', add: 'Add News or Media', edit: 'Edit Article', sub: host => `Shown on ${host}/news-media/ as soon as you publish` },
    video: { tile: 'YouTube or Vimeo link', add: 'Add a Video', edit: 'Edit Video', sub: host => `Paste a YouTube or Vimeo link — the video plays on ${host}/news-media/` },
    podcast: { tile: 'Spotify, Apple or any podcast link', add: 'Add a Podcast Link', edit: 'Edit Podcast Link', sub: () => 'Investors click through to the podcast — nothing to write, Investi fills in the details from the link' },
    webinar: { tile: 'Registration or recording link', add: 'Add a Webinar', edit: 'Edit Webinar', sub: () => 'Investors register from the website — afterwards you can swap the link for the recording' },
    coverage: { tile: 'Link to a news story about you', add: 'Add Media Coverage', edit: 'Edit Media Coverage', sub: (_, short) => `Link to a story about ${short} — investors read it on the publication’s website` },
};

const LINK_FORMS: Record<Exclude<NewsType, 'article'>, React.FC<LinkFormProps>> = { video: VideoForm, podcast: PodcastForm, webinar: WebinarForm, coverage: CoverageForm };

// the WordPress post's path, as the preview strip shows it
const slugPath = (title: string) => {
    const slug = title.toLowerCase().replace(/[‘’'“”"]/g, '').replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return `/news-media/${slug || 'new-item'}/`;
};

// Website Hub › News & media › Add / Edit (spec 409:10781 article, 420:13151 video, 412:13478 podcast, 420:13595 webinar, 420:14055 media coverage)
// ?type= picks the form; switching type keeps the title and publishing choice, each type's own form starts fresh.
const AddNews: React.FC = () => {
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { host, short } = useHubSite();
    const params = new URLSearchParams(search);
    const raw = params.get('type') ?? '';
    const type: NewsType = TYPES.find(t => t === raw) ?? 'article';
    const editing = /\/edit$/.test(pathname);
    const page = PAGES[type];

    const [title, setTitle] = useState(() => {
        if (!editing) return '';
        if (type === 'article') return getArticleSample(short).headline;
        return getLinkSample(type, short).title;
    });
    const [pictureFrom, setPictureFrom] = useState<string | null>(() => editing && type !== 'article' ? getLinkSample(type, short).pictureFrom : null);
    const [when, setWhen] = useState<PublishWhen>('now');

    const pickType = (t: NewsType) => {
        if (t === type) return;
        params.set('type', t);
        history.replace(`${pathname}?${params.toString()}`);
    };

    const back = () => history.push(hubPath('content', 'news-media'));
    const preview = () => toast.info(`Preview only: the WordPress preview of ${slugPath(title)} isn’t connected yet`);
    const saveDraft = () => { toast.info('Preview only: the draft wasn’t saved'); back(); };
    const publish = () => {
        if (!title.trim()) { toast.error(type === 'article' ? 'Add a headline first' : 'Add a title first — or paste a link and click Get Details'); return; }
        if (when === 'draft') { saveDraft(); return; }
        toast.info(when === 'schedule' ? `Preview only: nothing was scheduled on ${host}` : `Preview only: nothing was published to ${host}`);
        back();
    };

    const LinkForm = type === 'article' ? null : LINK_FORMS[type];
    // in the header; repeated at the foot of the page on mobile, where Account.scss hides .action-top
    const actions = <>
        <button type="button" className="btn-outline with-icon" onClick={preview}><FiEye /> Preview on the Website</button>
        <button type="button" className="btn-outline" onClick={saveDraft}>Save Draft</button>
        <button type="button" className="btn-primary with-icon" onClick={publish}>{type === 'coverage' && <FiCheck />} Publish to Website</button>
    </>;

    return <div className="hub-news-edit">
        <BackLink to={hubPath('content', 'news-media')} label="Back to News & media" />
        <HubHeader title={editing ? page.edit : page.add} sub={page.sub(host, short)}>{actions}</HubHeader>

        <div className="hub-preview-strip">
            <p>Preview opens the real page on {host} in a new tab — a WordPress preview link, so you see the live theme, not an Investi mock‑up. Drafts use a private preview link that only signed‑in editors can open.</p>
            <code>{slugPath(title)}</code>
        </div>

        <section className="card hub-type-card">
            <div className="section-title">
                <span className="icon-tile"><FiPlus /></span>
                <div className="text"><h2>What are you adding?</h2><div className="hint">Pick one — you can change it later</div></div>
            </div>
            <div className="hub-type-tiles" role="radiogroup" aria-label="What are you adding?">
                {TYPES.map(t => <button key={t} type="button" role="radio" aria-checked={t === type}
                    className={`hub-type-tile${t === type ? ' selected' : ''}`} onClick={() => pickType(t)}>
                    <span className="hub-type-ic">{NEWS_TYPES[t].icon}</span>
                    <span className="hub-type-radio" />
                    <strong>{NEWS_TYPES[t].label}</strong>
                    <span className="hub-type-sub">{PAGES[t].tile}</span>
                </button>)}
            </div>
        </section>

        <div className="cs-row hub-news-row">
            <div className="cs-main">
                {LinkForm
                    ? <LinkForm title={title} onTitle={setTitle} onPictureFrom={setPictureFrom} sample={editing} />
                    : <ArticleForm headline={title} onHeadline={setTitle} sample={editing} />}
            </div>
            <div className="cs-side">
                <PublishingCard when={when} onWhen={setWhen} />
                <PictureCard fromLink={pictureFrom} />
            </div>
        </div>

        {type === 'article' && <p className="hub-news-foot"><FiGlobe /> Publishing creates a WordPress post at {host}/news-media/… Editing it here later updates that same post, so the two never drift apart.</p>}
        <div className="action-bottom hub-news-actions">{actions}</div>
    </div>;
};

export default AddNews;
